import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  centavos,
  formatMoney,
  hoy,
  newId,
  parseMoney,
  type Centavos,
  type CuentaBancoCordobas,
} from '@abastece/shared';
import type { SaldoTelefonico } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { cuentaEnCordobas } from '../lib/bancos.js';
import { sesionAbierta } from '../lib/caja.js';
import { emitir } from '../lib/emitir.js';
import { otrosUsos, useUsosDeReferencias, yaPago } from '../lib/referencias.js';
import { usePuede } from '../lib/sesion.js';
import { Campo, Selector } from '../ui/Campo.js';
import { ElegirCuenta } from '../ui/ElegirCuenta.js';
import { Modal } from '../ui/Modal.js';
import { Pagina } from '../ui/Pagina.js';
import { FilaRecarga } from '../ui/FilasDeDinero.js';
import { VerTodos } from '../ui/FiltrosHistorial.js';
import { useEmpiezaElDia } from '../ui/LoDelDia.js';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';

const OPERADORAS = ['Claro', 'Tigo'];

/** The operators do not take dollars or cards for airtime, so the list is deliberately short. */
type FormaRecarga = 'efectivo_cordobas' | 'transferencia' | 'billetera';

/**
 * Recargas de saldo.
 *
 * A top-up is commission income, not a sale. C$100 of phone credit sold is roughly C$5 earned —
 * booking the face value as revenue would inflate sales twentyfold and make every margin report
 * meaningless, which is exactly why this lives here and not in the POS.
 */
export function Recargas() {
  const t = useT();
  const caja = useLiveQuery(() => sesionAbierta(), [], undefined);
  const empiezaElDia = useEmpiezaElDia();
  const saldos = useLiveQuery(() => db.saldos.toArray(), [], []);
  const recargas = useLiveQuery(() => db.recargas.reverse().limit(30).toArray(), [], []);
  const hoyEs = hoy();
  /** Today's commission, from every top-up of the day: the latest thirty listed below were all it added up. */
  const comisionHoy = useLiveQuery(
    async () => (await db.recargas.where('fecha').equals(hoyEs).toArray()).reduce((a, r) => a + r.comision, 0),
    [hoyEs],
    0,
  );

  const [operadora, setOperadora] = useState('Claro');
  const [telefono, setTelefono] = useState('');
  const [monto, setMonto] = useState('');
  const [formaPago, setFormaPago] = useState<FormaRecarga>('efectivo_cordobas');
  const [referencia, setReferencia] = useState('');
  const [comprando, setComprando] = useState(false);
  const [contando, setContando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const usos = useUsosDeReferencias();
  // Buying airtime is a purchase and counting it is a count: both the owner's.
  const puedeComprar = usePuede('recibir_compras');
  const puedeContar = usePuede('ajustar_inventario');

  const valor = parseMoney(monto);
  const saldo = saldos.find((s) => s.id === operadora.toLowerCase());
  // Paid by transfer or wallet, it waits in the queue, and the queue goes by the receipt.
  const enCola = formaPago !== 'efectivo_cordobas';
  const repetida = enCola ? otrosUsos(usos, referencia)[0] : undefined;
  const valido =
    telefono.trim().length >= 4 && valor !== null && valor > 0 && caja && (!enCola || referencia.trim() !== '');

  async function registrar() {
    if (!valido || !caja) return;
    setError(null);
    try {
      await emitir('recarga.registrada', {
        recargaId: newId(),
        sesionId: caja.id,
        operadora,
        telefono: telefono.trim(),
        montoFacial: valor!,
        formaPago,
        fecha: hoy(),
        ...(enCola ? { referencia: referencia.trim() } : {}),
      });
      setTelefono('');
      setMonto('');
      setReferencia('');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('recargas.noSePudo'));
    }
  }

  if (caja === undefined) {
    return (
      <Pagina titulo={t('recargas.titulo')}>
        <div className="tarjeta flex flex-col items-center gap-4 py-8 text-center">
          <p className="text-sm text-slate-400">{t('recargas.necesitaCaja')}</p>
          <Link to="/caja/empezar" className="btn-primario">
            <Ico icono="caja" />
            {t(empiezaElDia !== false ? 'turno.empezarDia' : 'turno.empezarTurno')}
          </Link>
        </div>
        {/* Selling waits for a till; looking one up does not. */}
        <div className="mt-6 flex justify-center">
          <Link to="/recargas/anteriores" className="btn-texto gap-1.5 text-sm text-slate-400" data-ver-todos="/recargas/anteriores">
            <Ico icono="recargas" className="size-4" />
            {t('recargasHist.titulo')}
          </Link>
        </div>
      </Pagina>
    );
  }

  return (
    <Pagina
      titulo={t('recargas.titulo')}
      subtitulo={
        comisionHoy > 0
          ? t('recargas.comisionHoy', { monto: formatMoney(comisionHoy as Centavos) })
          : undefined
      }
      acciones={
        puedeComprar && (
          <button
            type="button"
            className="btn-secundario gap-1.5 px-4 text-sm"
            onClick={() => setComprando(true)}
          >
            <Ico icono="recargas" className="size-4" />
            {t('recargas.comprarSaldo')}
          </button>
        )
      }
    >
      <section className="tarjeta mb-4 flex flex-col gap-4">
        <Selector
          etiqueta={t('recargas.operadora')}
          value={operadora}
          onChange={(e) => setOperadora(e.target.value)}
        >
          {OPERADORAS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Selector>

        <Campo
          etiqueta={t('comun.telefono')}
          inputMode="tel"
        maxLength={20}
          className="tabular"
          value={telefono}
          onChange={(e) => setTelefono(e.target.value)}
          autoComplete="off"
          placeholder="8888 7777"
        />

        <Campo
          etiqueta={t('recargas.monto')}
          inputMode="decimal"
          className="tabular text-lg"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
        />

        <Selector
          etiqueta={t('recargas.comoPaga')}
          value={formaPago}
          onChange={(e) => setFormaPago(e.target.value as FormaRecarga)}
        >
          <option value="efectivo_cordobas">{t('recargas.efectivo')}</option>
          <option value="transferencia">{t('pago.transferencia')}</option>
          <option value="billetera">{t('gastos.billeteraMovil')}</option>
        </Selector>

        {enCola && (
          <div>
            <Campo
              maxLength={80}
              etiqueta={t('cobro.referenciaObligatoria')}
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              autoComplete="off"
              ayuda={t('recargas.ayudaReferencia')}
            />
            {repetida && (
              <p className="mt-1.5 text-sm text-rose-300">
                {t(yaPago(repetida).clave, yaPago(repetida).valores)}
              </p>
            )}
          </div>
        )}

        {error && <p className="text-sm text-rose-400">{error}</p>}

        <button type="button" className="btn-primario" disabled={!valido} onClick={() => void registrar()}>
          <Ico icono="guardar" />
          {t('recargas.registrar')}
        </button>
      </section>

      <section className="tarjeta mb-4 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-slate-400">{t('recargas.saldoDisponible')}</h2>
          {puedeContar && (
            <button type="button" className="btn-texto gap-1 text-xs text-slate-300" onClick={() => setContando(true)}>
              <Ico icono="contar" className="size-3.5" />
              {t('recargas.contarSaldo')}
            </button>
          )}
        </div>
        {saldos.length === 0 ? (
          <p className="text-xs text-slate-500">{t('recargas.sinSaldo')}</p>
        ) : (
          saldos.map((s) => (
            <div key={s.id} className="flex items-baseline justify-between">
              <span className="text-sm">{s.operadora}</span>
              <span className={`tabular text-sm font-medium ${s.facial < 0 ? 'text-rose-400' : ''}`}>
                {formatMoney(s.facial)}
              </span>
            </div>
          ))
        )}
        {saldo && saldo.facial < 0 && (
          <p className="text-xs text-amber-300">
            {t('recargas.saldoNegativo', { operadora: saldo.operadora })}
          </p>
        )}
      </section>

      {recargas.length > 0 && (
        <section className="tarjeta">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-400">{t('recargas.ultimas')}</h2>
            <VerTodos to="/recargas/anteriores" femenino />
          </div>
          <ul className="flex flex-col divide-y divide-slate-800">
            {recargas.map((r) => (
              <FilaRecarga key={r.id} recarga={r} />
            ))}
          </ul>
        </section>
      )}

      {comprando && <ComprarSaldo onCerrar={() => setComprando(false)} />}
      {contando && <ContarSaldo saldos={saldos} onCerrar={() => setContando(false)} />}
    </Pagina>
  );
}

function ComprarSaldo({ onCerrar }: { onCerrar: () => void }) {
  const t = useT();
  const [operadora, setOperadora] = useState('Claro');
  const [facial, setFacial] = useState('');
  const [costo, setCosto] = useState('');
  const [formaPago, setFormaPago] = useState<FormaRecarga>('efectivo_cordobas');
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** The córdoba account a transfer pays from, once picked. */
  const [cuentaElegida, setCuentaElegida] = useState<CuentaBancoCordobas | null>(null);
  const cuenta = cuentaEnCordobas(config, cuentaElegida);

  const f = parseMoney(facial);
  const c = parseMoney(costo);
  // The reducer refuses it, and a refusal says nothing on screen.
  const costoMayor = f !== null && c !== null && c > f;
  const valido = f !== null && f > 0 && c !== null && c >= 0 && !costoMayor;
  const descuento = valido && f > 0 ? ((f - c) / f) * 100 : 0;

  return (
    <Modal
      titulo={t('recargas.comprarSaldo')}
      onCerrar={onCerrar}
      pie={
        <button
          type="button"
          className="btn-primario w-full"
          disabled={!valido}
          onClick={async () => {
            await emitir('recarga.saldo_comprado', {
              compraSaldoId: newId(),
              operadora,
              montoFacial: f!,
              costo: c!,
              formaPago,
              ...(formaPago === 'transferencia' ? { cuenta } : {}),
            });
            onCerrar();
          }}
        >
          <Ico icono="guardar" />
          {t('recargas.registrarCompra')}
        </button>
      }
    >
      <p className="mb-4 text-sm text-slate-400">
        {t('recargas.explicacionCompra')}
      </p>
      <div className="flex flex-col gap-4">
        <Selector
          etiqueta={t('recargas.operadora')}
          value={operadora}
          onChange={(e) => setOperadora(e.target.value)}
        >
          {OPERADORAS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Selector>
        <Campo
          etiqueta={t('recargas.valorFacial')}
          inputMode="decimal"
          className="tabular"
          value={facial}
          onChange={(e) => setFacial(e.target.value)}
          autoFocus
          placeholder="1000.00"
        />
        <Campo
          etiqueta={t('recargas.loQuePago')}
          inputMode="decimal"
          className="tabular"
          value={costo}
          onChange={(e) => setCosto(e.target.value)}
          placeholder="950.00"
          ayuda={valido ? t('recargas.descuentoDel', { pct: descuento.toFixed(1) }) : undefined}
          error={costoMayor ? t('recargas.costoMayor') : undefined}
        />
        <Selector
          etiqueta={t('recargas.comoPago')}
          value={formaPago}
          onChange={(e) => setFormaPago(e.target.value as FormaRecarga)}
        >
          <option value="efectivo_cordobas">{t('recargas.efectivo')}</option>
          <option value="transferencia">{t('pago.transferencia')}</option>
          <option value="billetera">{t('gastos.billeteraMovil')}</option>
        </Selector>
        {formaPago === 'transferencia' && (
          <ElegirCuenta valor={cuenta} onCambiar={(x) => setCuentaElegida(x as CuentaBancoCordobas)} etiqueta={t('bancos.pagaDe')} />
        )}
      </div>
    </Modal>
  );
}

/**
 * The operator's real balance against the books'. Written as the operator shows it — its portal
 * or its text message — and the difference is posted: short means top-ups went out that nobody
 * recorded, or were given away.
 */
function ContarSaldo({ saldos, onCerrar }: { saldos: readonly SaldoTelefonico[]; onCerrar: () => void }) {
  const t = useT();
  const [operadora, setOperadora] = useState('Claro');
  const [real, setReal] = useState('');
  const [nota, setNota] = useState('');
  const [resultado, setResultado] = useState<{ libros: Centavos; real: Centavos } | null>(null);
  const valor = parseMoney(real);
  const valido = valor !== null && valor >= 0;

  async function registrar() {
    if (!valido) return;
    const libros = saldos.find((s) => s.id === operadora.toLowerCase())?.facial ?? centavos(0);
    await emitir('recarga.saldo_contado', {
      conteoId: newId(),
      operadora,
      facial: valor!,
      ...(nota.trim() ? { nota: nota.trim() } : {}),
    });
    setResultado({ libros, real: valor! });
  }

  if (resultado) {
    const diferencia = resultado.real - resultado.libros;
    return (
      <Modal
        titulo={t('recargas.contarSaldo')}
        onCerrar={onCerrar}
        pie={
          <button type="button" className="btn-primario w-full" onClick={onCerrar}>
            <Ico icono="listo" />
            {t('comun.listo')}
          </button>
        }
      >
        <p className="tabular text-sm text-slate-400">
          {t('recargas.resultadoConteo', { libros: formatMoney(resultado.libros), real: formatMoney(resultado.real) })}
        </p>
        <p
          className={`tabular mt-3 text-2xl font-semibold ${diferencia < 0 ? 'text-rose-300' : diferencia > 0 ? 'text-amber-200' : 'text-emerald-300'}`}
        >
          {diferencia === 0
            ? t('recargas.cuadraSaldo')
            : t(diferencia < 0 ? 'recargas.faltaSaldo' : 'recargas.sobraSaldo', {
                monto: formatMoney(centavos(Math.abs(diferencia))),
              })}
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      titulo={t('recargas.contarSaldo')}
      onCerrar={onCerrar}
      pie={
        <button type="button" className="btn-primario w-full" disabled={!valido} onClick={() => void registrar()}>
          <Ico icono="contar" />
          {t('recargas.registrarConteo')}
        </button>
      }
    >
      <p className="mb-4 text-sm text-slate-400">{t('recargas.contarExplicacion')}</p>
      <div className="flex flex-col gap-4">
        <Selector etiqueta={t('recargas.operadora')} value={operadora} onChange={(e) => setOperadora(e.target.value)}>
          {OPERADORAS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Selector>
        <Campo
          etiqueta={t('recargas.saldoReal', { operadora })}
          inputMode="decimal"
          className="tabular"
          value={real}
          onChange={(e) => setReal(e.target.value)}
          autoFocus
          placeholder="0.00"
        />
        <Campo etiqueta={t('comun.nota')} value={nota} onChange={(e) => setNota(e.target.value)} maxLength={300} />
      </div>
    </Modal>
  );
}
